import { CloseOutlined, ReloadOutlined } from "@ant-design/icons";
import { Button, Modal, notification } from "antd";
import React, { useState } from "react";
import { useAppSelector } from "../../hooks/redux-hooks";
import { useVersionCheck } from "../../hooks/useVersionCheck";
import "./updatenotification.module.scss";

interface UpdateNotificationProps {
  autoShow?: boolean;
  showAsModal?: boolean;
}

const UpdateNotification: React.FC<UpdateNotificationProps> = ({ autoShow = true, showAsModal = false }) => {
  const { isNewVersionAvailable, forceReload } = useVersionCheck();
  const [dismissed, setDismissed] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [pendingUpdateDuringSession, setPendingUpdateDuringSession] = useState(false);

  // Check if there's an active recording session
  const { current_recording_id } = useAppSelector((state) => state.recordings.recordingDetail);
  const hasActiveSession = !!current_recording_id;

  React.useEffect(() => {
    if (isNewVersionAvailable && autoShow && !dismissed) {
      if (hasActiveSession) {
        // Session is active - store the pending update but don't show notification
        setPendingUpdateDuringSession(true);
      } else {
        // No active session - show notification immediately
        if (showAsModal) {
          setIsModalVisible(true);
        } else {
          showUpdateNotification();
        }
        setPendingUpdateDuringSession(false);
      }
    }
  }, [isNewVersionAvailable, autoShow, dismissed, showAsModal, hasActiveSession]);

  // Watch for session end to show pending updates
  React.useEffect(() => {
    if (!hasActiveSession && pendingUpdateDuringSession && !dismissed) {
      if (showAsModal) {
        setIsModalVisible(true);
      } else {
        showUpdateNotification();
      }
      setPendingUpdateDuringSession(false);
    }
  }, [hasActiveSession, pendingUpdateDuringSession, dismissed, showAsModal]);

  const showUpdateNotification = () => {
    notification.open({
      key: "app-update",
      message: "New Version Available",
      description: (
        <div>
          <p>A new version of the app is available. Please refresh to get the latest features and bug fixes.</p>
        </div>
      ),
      icon: <ReloadOutlined style={{ color: "#1890ff" }} />,
      placement: "topRight",
      duration: 0, // Don't auto-close
      btn: (
        <div style={{ display: "flex", gap: "8px" }}>
          <Button type="primary" size="small" onClick={handleRefresh} icon={<ReloadOutlined />}>
            Refresh Now
          </Button>
        </div>
      ),
      onClose: handleDismiss,
    });
  };

  const handleRefresh = () => {
    notification.destroy("app-update");
    setIsModalVisible(false);
    forceReload();
  };

  const handleDismiss = () => {
    notification.destroy("app-update");
    setIsModalVisible(false);
    setDismissed(true);

    // Auto-show again after 30 minutes
    setTimeout(
      () => {
        setDismissed(false);
      },
      30 * 60 * 1000,
    );
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    handleDismiss();
  };

  if (showAsModal) {
    return (
      <Modal
        title="🚀 New Version Available"
        open={isModalVisible}
        onCancel={handleModalCancel}
        centered
        footer={[
          <Button key="refresh" type="primary" onClick={handleRefresh} icon={<ReloadOutlined />}>
            Refresh Now
          </Button>,
        ]}
        closable={false}
        maskClosable={false}
      >
        <div>
          <p>A new version of Nudge AI is available with the latest features and improvements.</p>
          <p>Please refresh your browser to get the update.</p>
        </div>
      </Modal>
    );
  }

  return null; // Notification-only mode
};

export default UpdateNotification;
